import s from './EventCard.module.sass'
import React, { FC, useRef } from 'react'
import { useAppSelector } from '@app/model/store'
import { Favorite } from '@features/Favorite'

import { ErrorBoundary } from '@shared/lib/components/ErrorBoundary'
import { useIntersectionAnalytic } from '@shared/lib/hooks/useIntersectionAnalytic'
import { PickAnalytic } from '@shared/api/analytics/types/types'
import { ChevronWithTooltip } from '@shared/ui/Feedback/ChevronWithTooltip'
import { IconButton } from '@shared/ui/Actions/IconButton'
import { EGG } from '@shared/api/analytics'

import IconChevron from '@icons/icon-chevron-medium.svg'
import IconLike from '@icons/icon-like.svg'
import { CardTwoBlock } from '@shared/ui/Layout/CardTwoBlock'
import { Text } from '@shared/ui/Typography/Text'
import { Button } from '@shared/ui/Actions/Button'

interface Props {
  data: any
  analytic: PickAnalytic<'section_index' | 'object_id'>
}

declare const window: Window &
  typeof globalThis & {
    ticketManager: any
  }

const PAYMENT_WIDGET_KEY = '0d8c1d07-209c-497f-9a32-620dd437758a'
const UTM_ACTIVITY = '?utm_source=restaurants&utm_medium=referral&utm_campaign=afisha_events'

const EventCard: FC<Props> = ({ data, analytic }) => {
  const element = useRef()

  const activityType =
    data?.input?.concert ||
    data?.input?.exhibition ||
    data?.input?.movie ||
    data?.input?.performance ||
    data?.input?.sport

  const id = activityType?.id
  const idNumber = id ? parseInt(id.match(/\d+/)) : null
  const imageSrc = activityType?.image?.url
  const imageAlt = activityType?.name || ''
  const isAfishaChoice = !!activityType?.afishaChoice

  const userAfishaId = useAppSelector((state) => state.global.user.data?.afisha_id)

  const handleOpenActivityLink = (data, ev) => {
    try {
      ev?.stopPropagation()

      const thisURL = data?.url || null

      EGG.pages.RestItem.event_card_click(analytic)

      const needToOpenUrl = thisURL && ev.target.dataset.button !== 'x-open-payment'
      const target = needToOpenUrl ? 'https://afisha.ru' + thisURL + UTM_ACTIVITY : null

      if (needToOpenUrl) {
        window.location.href = target
      }
    } catch (e) {
      console.error(e)
      return false
    }
  }

  /**
   * @function handlePaymentAction — работа с window.ticketManager
   * @todo вынести в redux для вызова session с покупками
   * @todo добавить callback, если ticketManager не загрузился,
   *    вместо этого открывать страницу покупки на Афише
   */
  const handleOpenWidget = (data, e) => {
    e.stopPropagation()
    EGG.pages.RestItem.event_price_click(analytic)

    try {
      const sessionId = data?.input?.sessions[0]?.sessionSourceID
      if (sessionId && typeof window.ticketManager !== 'undefined') {
        console.dev('handleOpenWidget ticketManager:', window.ticketManager, PAYMENT_WIDGET_KEY, sessionId)
        window.ticketManager.session(PAYMENT_WIDGET_KEY, sessionId)

        if (userAfishaId) {
          setTimeout(() => {
            if (window.ticketManager?.widgetFrame?.contentWindow) {
              const message = { authorized: true }
              console.dev(
                `handleOpenWidget: send message ${JSON.stringify(message)} to`,
                window.ticketManager.widgetFrame.contentWindow,
              )
              window.ticketManager.widgetFrame.contentWindow.postMessage(message)
            }
          }, 1000)
        }
      } else {
        throw new Error(e)
      }
    } catch (e) {
      console.error(e)
      return false
    }
  }

  useIntersectionAnalytic(element, () => {
    EGG.pages.RestItem.event_card_view(analytic)
  })

  const favCategory = data?.input?.concert
    ? 'concert'
    : data?.input?.exhibition
    ? 'exhibition'
    : data?.input?.movie
    ? 'movie'
    : data?.input?.performance
    ? 'performance'
    : data?.input?.sport
    ? 'sport'
    : ''

  return (
    <ErrorBoundary>
      <CardTwoBlock sizes={'L ML'} ref={element}>
        <CardTwoBlock.Img
          img={imageSrc}
          classNameContent={s['payment-item-open-modal-wrap']}
          alt={imageAlt}
          onClickInsteadLink={(e) => handleOpenActivityLink(activityType, e)}>
          {isAfishaChoice && <ChevronWithTooltip icon={IconChevron} className={s['payment-item-afisha-selected']} />}
          {data.amount && (
            <Button
              mode={'white'}
              sizes={'S'}
              data-button='x-open-payment'
              onClick={(e) => handleOpenWidget(data, e)}
              className={s['payment-item-open-modal']}>
              от {data.amount} ₽
            </Button>
          )}

          <Favorite
            // @ts-ignore
            category={favCategory}
            id={idNumber}
            analytic={{ data: activityType, other: { section_name: 'events' } }}
            className={s.favorite}>
            <IconButton icon={IconLike} sizes='S' mode={'white'} />
          </Favorite>
        </CardTwoBlock.Img>
        <CardTwoBlock.Content>
          {data.name && (
            <Text
              sizes={'L'}
              tag={'h3'}
              className={s['payment-name']}
              onClick={(ev) => handleOpenActivityLink(activityType, ev)}>
              {data.name}
            </Text>
          )}

          {data.type && (
            <Text sizes={'S'} className={s['payment-genre']}>
              {data.type}
            </Text>
          )}
          {data.timeDescr && (
            <Text sizes={'S'} className={s['payment-time-descr']}>
              {data.timeDescr}
            </Text>
          )}
        </CardTwoBlock.Content>
      </CardTwoBlock>
    </ErrorBoundary>
  )
}

export default EventCard
